import { Inject, Injectable, signal } from '@angular/core';
import { RestService } from '../../../core/services/rest.service';

import * as XLSX from 'xlsx';

const KEY_TO_GROUP: Record<string, string> = {
    // CONNECTABLE_BUILDINGS
    system_selection_mode: 'CONNECTABLE_BUILDINGS',
    minimum_heat_demand: 'CONNECTABLE_BUILDINGS',
    heating_energies: 'CONNECTABLE_BUILDINGS',
    sectors: 'CONNECTABLE_BUILDINGS',
    branchs: 'CONNECTABLE_BUILDINGS',
    sub_branchs: 'CONNECTABLE_BUILDINGS',
    owners: 'CONNECTABLE_BUILDINGS',
    housing_types: 'CONNECTABLE_BUILDINGS',

    // OPPORTUNITY_ZONE
    maximum_pairing_distance: 'OPPORTUNITY_ZONE',
    minimum_heat_density: 'OPPORTUNITY_ZONE',
    distance_method: 'OPPORTUNITY_ZONE',
    closing_radius: 'OPPORTUNITY_ZONE',

    // TECHNICAL_SUPPORT
    looking_for_technical_support: 'TECHNICAL_SUPPORT',
    dist_max: 'TECHNICAL_SUPPORT',
    resolution_raster: 'TECHNICAL_SUPPORT',

    // NETWORK
    cutoff: 'NETWORK',
    max_importance_road_to_project_building: 'NETWORK',

    // HEAT_SOURCE
    n_candidate: 'HEAT_SOURCE',
    density_threshold: 'HEAT_SOURCE',

    // OPTIMAL_HEAT_NETWORK
    heat_network_density_objective: 'OPTIMAL_HEAT_NETWORK',

    // REPORT
    production_type: 'REPORT',
    renewable_share: 'REPORT',
    full_load_hours: 'REPORT',
    demand_scenario: 'REPORT',
    gas_mix_scenario: 'REPORT',
    gas_fuel_cost_scenario: 'REPORT',
    climate_scenario: 'REPORT',
    annual_discount_rate: 'REPORT',
    project_duration: 'REPORT',
    developer_margin: 'REPORT',
    annual_loan_rate: 'REPORT',
    loan_duration: 'REPORT',
    depreciation_period: 'REPORT',
    region_code: 'REPORT',
    population_class: 'REPORT',
};

type ValueType = 'string' | 'number' | 'integer' | 'float' | 'boolean' | 'array';

interface ValidationRule {
    label?: string;
    type: ValueType;
    required?: boolean;
    ge?: number;
    le?: number;
    gt?: number;
    lt?: number;
    enum?: any[];
    items?: ValidationRule;
}

const VALIDATION_SCHEMA: Record<string, ValidationRule> = {
    // Connectable Buildings
    system_selection_mode: {
        label: 'Mode de sélection',
        type: 'string',
        required: true,
        enum: ['greedy', 'sample'],
    },
    minimum_heat_demand: {
        label: 'Besoin de chaleur annuel minimum',
        type: 'float',
        required: true,
        ge: 0,
    },
    heating_energies: {
        label: 'Énergies de chauffage actuelles',
        type: 'array',
        required: true,
        items: {
            type: 'string',
            enum: ['Electricité', 'Gaz', 'Fioul', 'RCU', 'GPL'],
        },
    },
    sectors: {
        label: "Secteurs d'activité ciblés",
        type: 'array',
        required: true,
        items: {
            type: 'string',
            enum: ['Public', 'Privé', 'NR'],
        },
    },
    branchs: {
        label: "Branches d'activité ciblées",
        type: 'array',
        required: true,
        items: {
            type: 'string',
            enum: [
                'Cafés, hôtels, restaurants',
                'Habitat communautaire',
                'Santé',
                'Enseignement',
                'Sport, culture, loisirs, équipements collectifs divers',
                'Bureaux et administration',
                'Commerce',
                'Transport',
            ],
        },
    },
    sub_branchs: {
        label: "Sous branches d'activités ciblées",
        type: 'array',
        required: true,
        items: {
            type: 'string',
            enum: [
                'Activités avec process',
                'Activités hospitalières',
                'Autre habitat communautaire',
                'Autres activités de la branche',
                'Cafés, cantines, traiteurs',
                'Commerce de détail non alimentaire',
                'Commerce de gros',
                'Enseignement primaire',
                'Enseignement secondaire',
                'Enseignement supérieur, recherche',
                'Entrepôts',
                'Grand commerce alimentaire',
                'Hôtels et hébergement similaire',
                'Le reste du secteur privé',
                'Le secteur privé fortement informatisé',
                "Les bureaux de l'État",
                'Les bureaux des collectivités',
                'Locaux culturels',
                'Locaux sportifs',
                'Maisons de retraite',
                'Petit commerce alimentaire',
                'Restaurants',
                'Transport',
                'Équipements collectifs',
                'NR',
            ],
        },
    },
    owners: {
        label: 'Types de propriétaire',
        type: 'array',
        required: true,
        items: {
            type: 'string',
            enum: [
                'Etat',
                'Région',
                'Département',
                'Commune',
                'Office HLM',
                'Etablissements publics ou organismes associés',
                'Personnes morales non remarquables',
                "Personnes morales représentant des sociétés d'économie mixte",
                'Copropriétaires',
                'Associés',
            ],
        },
    },
    housing_types: {
        label: 'Types de logement',
        type: 'array',
        required: true,
        items: {
            type: 'string',
            enum: ['Maison individuelle', 'Logement collectif', 'HLM'],
        },
    },

    // Opportunity Zone
    maximum_pairing_distance: {
        label: 'Distance max entre voisins (m)',
        type: 'float',
        required: true,
        gt: 0,
    },
    minimum_heat_density: {
        label: 'Densité thermique min (kWh/m)',
        type: 'float',
        required: true,
        gt: 0,
    },
    distance_method: {
        label: 'Méthode de calcul des distances',
        type: 'string',
        required: true,
        enum: ['centroid', 'polygon'],
    },
    closing_radius: {
        label: 'Rayon de lissage (m)',
        type: 'float',
        required: true,
        ge: 0,
    },

    // Technical Support
    looking_for_technical_support: {
        label: 'Création des supports pleine terre',
        type: 'boolean',
        required: true,
    },
    dist_max: {
        label: 'Distance max de zone (m)',
        type: 'float',
        required: true,
        gt: 0,
    },
    resolution_raster: {
        label: 'Précision du terrain (m)',
        type: 'number',
        required: true,
        gt: 0,
    },

    // Network
    cutoff: {
        label: 'Seuil de coupure (m)',
        type: 'float',
        required: true,
        gt: 0,
    },
    max_importance_road_to_project_building: {
        label: 'Priorité aux axes routiers',
        type: 'number',
        required: true,
        ge: 1,
        le: 6,
    },

    // Heat Source
    n_candidate: {
        label: 'Nombre de sites candidats',
        type: 'number',
        required: true,
        gt: 0,
    },
    density_threshold: {
        label: 'Seuil de densité bâtie',
        type: 'float',
        required: true,
        ge: 0,
        le: 1,
    },

    // Optimal Heat Network
    heat_network_density_objective: {
        label: 'Objectif de densité thermique linéaire (kWh/ml)',
        type: 'float',
        required: true,
        gt: 0,
    },

    // Report
    production_type: {
        label: 'Mix de production',
        type: 'string',
        required: true,
        enum: [
            'Chaudière biomasse indifférenciée (bois énergie)',
            'Geothermie champs de sonde',
            'Geothermie sur aquifères superficiels',
            'Géothermie profonde',
            'UIOM (UVE)',
            'Chaleur fatale',
            'Chaudière biomasse <= 50KW',
            'Chaudière biomasse <= 200KW',
            'Chaudière biomasse <= 800KW',
            'Chaudière biomasse > 800KW',
        ],
    },
    renewable_share: {
        label: "Taux d'EnR&R (%)",
        type: 'float',
        required: true,
        ge: 0,
        le: 1,
    },
    full_load_hours: {
        label: 'Heures équivalentes pleine puissance',
        type: 'float',
        required: true,
        ge: 0,
        le: 8670,
    },
    demand_scenario: {
        label: "Scénario d'évolution de la demande",
        type: 'string',
        required: true,
        enum: ['scenario_ademe_s2', 'scenario_ademe_s3'],
    },
    gas_fuel_cost_scenario: {
        label: 'Scénarios de prix du gaz',
        type: 'string',
        required: true,
        enum: ['scenario_bas', 'scenario_moyen', 'scenario_haut'],
    },
    gas_mix_scenario: {
        label: "Scénario d'évolution du mix du gaz",
        type: 'string',
        required: true,
        enum: [
            'scenario_haut_gr',
            'scenario_territoires_ajustes',
            'scenario_ademe_s1',
            'scenario_ademe_s2',
            'scenario_ademe_s3',
        ],
    },
    climate_scenario: {
        label: 'Scénario climatique',
        type: 'string',
        required: true,
        enum: ['scenario_rcp26', 'scenario_rcp45', 'scenario_rcp85'],
    },
    annual_discount_rate: {
        label: "Taux d'actualisation (%)",
        type: 'float',
        required: true,
        ge: 0,
        le: 1,
    },
    project_duration: {
        label: "Durée d'analyse (années)",
        type: 'number',
        required: true,
        ge: 0,
    },
    developer_margin: {
        label: "Marge de l'opérateur (%)",
        type: 'float',
        required: true,
        ge: 0,
        le: 1,
    },
    annual_loan_rate: {
        label: "Conditions d'emprunt",
        type: 'float',
        required: true,
        ge: 0,
        le: 1,
    },
    loan_duration: {
        label: "Durée de l'emprunt (années)",
        type: 'number',
        required: true,
        ge: 0,
    },
    depreciation_period: {
        label: "Durée d'amortissement",
        type: 'number',
        required: true,
        ge: 0,
    },
    region_code: { type: 'string', required: true },
};

export interface ParameterError {
    label: string;
    errors: string[];
}

@Injectable({
    providedIn: 'root',
})
export class SimulationNewNetworkService {
    gpkgFile = signal<File | null>(null);
    xlsxFile = signal<File | null>(null);
    prospectParams: any = null;
    xlsxErrors: ParameterError[] = [];

    readonly gpkgResetSignal = signal(0);
    readonly xlsxResetSignal = signal(0);

    constructor(@Inject(RestService) private restService: RestService) {}

    setGpkgFile(file: File | null) {
        this.gpkgFile.set(file);

        if (file === null) {
            this.gpkgResetSignal.update((v) => v + 1);
        }
    }

    clearGpkgFile() {
        this.setGpkgFile(null);
    }

    setXlsxFile(file: File | null) {
        this.xlsxFile.set(file);

        if (file === null) {
            this.xlsxResetSignal.update((v) => v + 1);
        }
    }

    clearXlsxFile() {
        this.setXlsxFile(null);
    }

    clearProspectParams() {
        this.prospectParams = null;
    }

    resetSimulation() {
        this.clearGpkgFile();
        this.clearXlsxFile();
    }

    canRunNewNetworkSimulation(): boolean {
        return (
            !!this.gpkgFile() &&
            !!this.xlsxFile() &&
            !!this.prospectParams &&
            this.xlsxErrors.length === 0
        );
    }

    async simulateNewNetwork(): Promise<void> {
        if (!this.canRunNewNetworkSimulation()) {
            throw new Error('Fichiers manquants pour lancer la simulation');
        }

        const file = this.gpkgFile()!;
        const prospectParams = JSON.stringify(this.prospectParams);

        const formData = new FormData();
        formData.append('file', file);
        formData.append('prospectParams', prospectParams);

        const blob = await this.restService.postBlob('/simulation/new-network', formData);
        const fileName = file.name.substring(0, file.name.lastIndexOf('.'));
        const safeName = fileName.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        this.downloadBlob(blob, `resultat_${safeName}.zip`);
    }

    private downloadBlob(blob: Blob, filename: string) {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
    }

    async extractApiFormatFromExcel(file: File) {
        const arrayBuffer = await file.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });
        const sheetName = 'api_format';

        if (!workbook.SheetNames.includes(sheetName)) {
            throw new Error(`L'onglet "${sheetName}" est introuvable`);
        }

        const worksheet = workbook.Sheets[sheetName];
        const rows = XLSX.utils.sheet_to_json<any[]>(worksheet, { header: 1, defval: null });

        const { payload, errors } = this.buildSimulationPayload(rows);

        this.prospectParams = {
            connectable_buildings: payload['CONNECTABLE_BUILDINGS'],
            opportunity_zone: payload['OPPORTUNITY_ZONE'],
            technical_support: payload['TECHNICAL_SUPPORT'],
            network: payload['NETWORK'],
            heat_source: payload['HEAT_SOURCE'],
            optimal_heat_network: payload['OPTIMAL_HEAT_NETWORK'],
            report: payload['REPORT'],
        };
        this.prospectParams.report['region_code'] = '76';

        this.xlsxErrors = errors;

        return this.xlsxErrors;
    }

    buildSimulationPayload(rows: any[][]) {
        const payload: Record<string, any> = {};
        const errors: ParameterError[] = [];

        rows.forEach(([key, rawValue]) => {
            if (!key) return;

            const group = KEY_TO_GROUP[key];

            if (!group) {
                console.warn(`Clé inconnue ignorée : ${key}`);
                return;
            }

            if (!payload[group]) {
                payload[group] = {};
            }

            const parsedValue = this.parseValueByKey(key, rawValue);
            const validationErrors = this.validateValue(key, parsedValue);

            if (validationErrors.length) {
                const label = VALIDATION_SCHEMA[key].label!;
                errors.push({
                    label: label,
                    errors: validationErrors,
                });
            } else {
                payload[group][key] = parsedValue;
            }
        });

        return { payload, errors };
    }

    parseValueByKey(key: string, rawValue: any): any {
        if (key === 'looking_for_technical_support') {
            return rawValue === 1 || rawValue === '1' || rawValue === true;
        }

        if (typeof rawValue === 'string') {
            const v = rawValue.trim();

            if (!isNaN(Number(v))) return Number(v);

            if ((v.startsWith('[') && v.endsWith(']')) || (v.startsWith('{') && v.endsWith('}'))) {
                try {
                    return JSON.parse(v);
                } catch {
                    return rawValue;
                }
            }
        }

        return rawValue;
    }

    private validateValue(key: string, value: any, ruleOverride?: ValidationRule): string[] {
        const rule = ruleOverride ?? VALIDATION_SCHEMA[key];
        const errors: string[] = [];

        if (!rule) return errors;

        switch (rule.type) {
            case 'number':
                if (typeof value !== 'number') {
                    errors.push(`La valeur doit être un nombre entier.`);
                }
                break;
            case 'float':
                if (typeof value !== 'number') {
                    errors.push(`La valeur doit être un nombre décimal.`);
                }
                break;
            case 'string':
                if (typeof value !== 'string') {
                    errors.push(`La valeur doit être une chaîne de caractère.`);
                }
                break;
            case 'boolean':
                if (typeof value !== 'boolean') {
                    errors.push(`La valeur doit être un booléen.`);
                }
                break;
            case 'array':
                if (!Array.isArray(value)) {
                    errors.push(`La valeur doit être une liste.`);
                }
                break;
        }

        if (rule.enum && !rule.enum.includes(value)) {
            errors.push(`La valeur doit être une de: ${rule.enum.join(', ')}.`);
        }

        if (typeof rule.ge === 'number' && value < rule.ge) {
            errors.push(`La valeur doit être ≥ ${rule.ge}.`);
        }

        if (typeof rule.le === 'number' && value > rule.le) {
            errors.push(`La valeur doit être ≤ ${rule.le}.`);
        }

        if (typeof rule.gt === 'number' && value <= rule.gt) {
            errors.push(`La valeur doit être > ${rule.gt}.`);
        }

        if (typeof rule.lt === 'number' && value >= rule.lt) {
            errors.push(`La valeur doit être < ${rule.lt}.`);
        }

        if (rule.items) {
            if (!Array.isArray(value)) {
                errors.push('La valeur doit être une liste.');
            }

            if (value.length === 0) {
                errors.push('La liste ne doit pas être vide.');
            }

            value.forEach((item: string, index: number) => {
                const errorsItems = this.validateValue(`${key}[${index}]`, item, rule.items);

                if (errorsItems.length) {
                    errors.push(...errorsItems);
                }
            });
        }

        return errors;
    }
}
